import { Button, Fade, IconButton, Paper, Typography } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'

import NoDataImage from '../../assets/images/No-Data.png'
import { getTranslate } from '../../localization'
import { InventoryComparisonRow } from '../../types/inventoryHealth'
import InventoryHealthRow from './InventoryHealthRow'
import InventoryHealthRowSkeleton from './InventoryHealthRow.skeleton'
import useStyles from './styles/InventoryHealthTable.styles'

type Props = {
  rows: InventoryComparisonRow[]
  loading: boolean
  onRefresh: () => void
  /** Loaded before column filtering, so an empty table can say WHY. */
  loadedCount: number
  onClearFilters: () => void
}

const HEADINGS = [
  'Date',
  'Room type',
  'Rate plan',
  'Channel',
  'PMS',
  'CRS',
  'Channel value',
  'Assessment'
]

export default function InventoryHealthTable({
  rows,
  loading,
  onRefresh,
  loadedCount,
  onClearFilters
}: Props) {
  const classes = useStyles()

  return (
    <Paper
      className={classes.root}
      role="table"
      aria-label={getTranslate('Layer comparison')}>
      <div className={classes.tableHead} role="row">
        {HEADINGS.map((heading, index) => (
          <div key={heading} role="columnheader">
            {index === HEADINGS.length - 1 ? (
              <span>
                <Typography variant="body1" component="span">
                  {getTranslate(heading)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={onRefresh}
                  aria-label={getTranslate('Refresh')}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            ) : (
              <Typography variant="body1">{getTranslate(heading)}</Typography>
            )}
          </div>
        ))}
      </div>

      {loading &&
        Array(5)
          .fill('')
          .map((_, index) => <InventoryHealthRowSkeleton key={index} />)}

      {!loading && rows.length === 0 && (
        <div className={classes.emptyState}>
          <img
            src={NoDataImage}
            alt=""
            className={classes.emptyImage}
            aria-hidden="true"
          />
          {/*
            Filtered-empty and server-empty are different situations with
            different remedies, and collapsing them into one sentence is the
            false-reassurance failure this screen exists to remove — in a
            smaller place. Only one of them is recoverable from here.
          */}
          {loadedCount > 0 ? (
            <>
              <Typography variant="body2" color="textSecondary">
                {getTranslate(
                  'No rows match the current column filters. {total} rows are loaded.',
                  { total: loadedCount }
                )}
              </Typography>
              <Button onClick={onClearFilters}>
                {getTranslate('Clear filters')}
              </Button>
            </>
          ) : (
            <Typography variant="body2" color="textSecondary">
              {getTranslate('No inventory was returned for this selection.')}
            </Typography>
          )}
        </div>
      )}

      {!loading &&
        rows.map((row) => (
          <Fade
            in
            timeout={600}
            key={`${row.date}-${row.roomTypeCode}-${row.ratePlanCode}-${row.channelID}`}>
            <div>
              <InventoryHealthRow row={row} />
            </div>
          </Fade>
        ))}
    </Paper>
  )
}
